import axios, {
  AxiosInstance,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from 'axios';
import { BASE_URL } from './commonConstants';
import {
  addTokenCookies,
  fetchRefreshToken,
  getTokenCookies,
  removeTokenCookies,
  ROUTES,
} from '@jobcost-monorepo/utils';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Timeout di default delle chiamate al backend: senza, una richiesta che si
 * impianta lascia l'interfaccia in caricamento per sempre invece di mostrare
 * un errore.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 60_000;

/**
 * Timeout per le operazioni batch che orchestrano molte chiamate esterne (es.
 * import autofatture). Sta appena sotto il `proxy_read_timeout` di nginx (300s),
 * così l'errore lo genera il client con un messaggio utile invece di arrivare
 * come risposta vuota dal proxy.
 */
export const LONG_REQUEST_TIMEOUT_MS = 290_000;

/**
 * Timeout della fattura Previxio (`POST /invoicing/timesheet-invoices`): poco
 * sopra il budget del backend (`PREVIXIO_TOTAL_BUDGET_MS`, default 50 s), che a
 * sua volta resta sotto i ~60 s dopo cui il proxy di Fly chiude una
 * connessione inattiva.
 */
export const INVOICING_REQUEST_TIMEOUT_MS = 55_000;

export abstract class HttpClient {
  protected instance: AxiosInstance | undefined;

  protected createInstance(
    timeout: number = DEFAULT_REQUEST_TIMEOUT_MS,
  ): AxiosInstance {
    this.instance = axios.create({
      baseURL: BASE_URL,
      timeout,
      headers: {
        'Content-Type': 'application/json',
      } as AxiosRequestHeaders,
    });
    this.initializeInterceptor().then();
    return this.instance;
  }

  private initializeInterceptor = async () => {
    const accessToken = getTokenCookies().accessToken;
    this.instance?.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          config.headers = {
            Authorization: `Bearer ${accessToken}`,
          } as AxiosRequestHeaders;
        }
        return config;
      },
    );

    this.instance?.interceptors.response.use(
      (res: AxiosResponse) => {
        return res;
      },
      async (err: AxiosError) => {
        const originalConfig = err.config as RetryableRequestConfig;

        if (err.response) {
          if (err.response.status === 401 && !originalConfig._retry) {
            originalConfig._retry = true;

            try {
              const { refreshToken } = getTokenCookies();
              if (refreshToken) {
                const result = await fetchRefreshToken(refreshToken);
                const { token, refresh } = result || {};
                if (token && refresh) {
                  addTokenCookies({
                    accessToken: token,
                    refreshToken: refresh,
                  });
                  return this.instance?.request(originalConfig);
                } else {
                  removeTokenCookies();
                  window.location.assign(ROUTES.SIGN_IN);
                  return Promise.reject(err);
                }
              }
              // @ts-ignore
            } catch (e: never) {
              if (e.response && e.response?.data) {
                return Promise.reject(e.response?.data);
              }

              return Promise.reject(e);
            }
          }
        }

        return Promise.reject(err);
      },
    );
  };
}
